import { useEffect, useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { label } from "@/lib/api";

const TONE = {
  blue: "bg-blue-50 text-blue-700 border-blue-200", indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200", sky: "bg-sky-50 text-sky-700 border-sky-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200", green: "bg-green-100 text-green-800 border-green-300",
  rose: "bg-rose-50 text-rose-700 border-rose-200", slate: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_TONE = {
  NEW: "blue", CONTACTED: "indigo", FOLLOW_UP: "indigo", DOCUMENT_PENDING: "amber", ELIGIBILITY_CHECK: "sky", KYC_PENDING: "amber",
  KYC_COMPLETED: "sky", LENDER_SUBMITTED: "sky", OFFER_RECEIVED: "sky", CUSTOMER_ACCEPTED: "emerald", SANCTIONED: "emerald",
  DISBURSEMENT_PENDING: "emerald", DISBURSED: "green", PAYOUT_PENDING: "amber", PAYOUT_PAID: "green", REJECTED: "rose", DROPPED: "slate",
  CLOSED: "slate", LEAD_CREATED: "blue", ELIGIBILITY: "sky", KYC_INITIATED: "amber", CREDIT_CHECK: "sky", LENDER_SUBMISSION: "sky",
  SANCTION: "emerald", DISBURSEMENT: "emerald", PENDING: "amber", APPROVED: "emerald", PROCESSING: "sky", PAID: "green", HOLD: "slate",
  VERIFIED: "emerald", FAILED: "rose", UPLOADED: "blue", ONLINE: "emerald", OFFLINE: "rose", ERROR: "rose", UNKNOWN: "slate",
  PROCESSED: "emerald", DEAD_LETTER: "rose", REJECTED_SIGNATURE: "rose", RECEIVED: "blue", OPEN: "amber", RESOLVED: "emerald",
  AVAILABLE: "sky", ACCEPTED: "emerald", NOT_SELECTED: "slate", EXPIRED: "slate", SUSPENDED: "rose", GRANTED: "emerald", HIGH: "rose", MEDIUM: "amber", LOW: "slate",
};

export function StatusBadge({ value, testid }) {
  const t = TONE[STATUS_TONE[value] || "slate"];
  return <span data-testid={testid} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${t}`}>{label(value)}</span>;
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 md:text-3xl" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function Card({ title, action, children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-sm font-semibold text-slate-800">{title}</h3>
          {action}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}

export function Kpi({ label: l, value, hint, tone = "text-slate-900", testid, icon: Icon }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{l}</p>
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
      </div>
      <p className={`mt-2 font-display text-2xl font-bold ${tone}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export function Field({ label: l, name, form, setForm, type = "text", options, required, placeholder, hint, disabled, className = "" }) {
  const v = form[name] ?? "";
  const on = (e) => setForm({ ...form, [name]: type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value });
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-xs font-semibold text-slate-600">{l}{required && <span className="text-rose-500"> *</span>}</span>
      {options ? (
        <select data-testid={`field-${name}`} className="fo-input" value={v} onChange={on} disabled={disabled}>
          <option value="">Select…</option>
          {options.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{label(o)}</option>))}
        </select>
      ) : type === "textarea" ? (
        <textarea data-testid={`field-${name}`} className="fo-input" rows={3} value={v} onChange={on} placeholder={placeholder} disabled={disabled} />
      ) : (
        <input data-testid={`field-${name}`} className="fo-input" type={type} value={v} onChange={on} placeholder={placeholder} disabled={disabled} />
      )}
      {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

export function Table({ cols, rows, empty = "No records found", testid, onRow }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
          <tr>{cols.map((c) => <th key={c.k} className="whitespace-nowrap px-4 py-3 font-semibold">{c.h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-slate-400">{empty}</td></tr>}
          {rows.map((r, i) => (
            <tr key={r.id || i} onClick={onRow ? () => onRow(r) : undefined} className={`border-t border-slate-100 ${onRow ? "cursor-pointer hover:bg-slate-50" : ""}`}>
              {cols.map((c) => <td key={c.k} className="whitespace-nowrap px-4 py-3 text-slate-700">{c.r ? c.r(r) : r[c.k] ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pager({ page, size, total, setPage }) {
  const pages = Math.max(1, Math.ceil(total / size));
  return (
    <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
      <span data-testid="pager-total">{total} records</span>
      <div className="flex items-center gap-2">
        <button data-testid="pager-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded border px-3 py-1 disabled:opacity-40">Prev</button>
        <span>{page} / {pages}</span>
        <button data-testid="pager-next" disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded border px-3 py-1 disabled:opacity-40">Next</button>
      </div>
    </div>
  );
}

export const Spinner = ({ className = "" }) => <Loader2 className={`h-5 w-5 animate-spin text-blue-800 ${className}`} />;
export const Loading = () => <div className="flex h-60 items-center justify-center"><Spinner /></div>;

export function Logo({ dark }) {
  return (
    <div className={`flex items-center ${dark ? "rounded-lg bg-white px-2 py-1" : ""}`} data-testid="brand-logo">
      <img src="/images/logo.png" alt="FinOrbit Finance Pvt Ltd" className={dark ? "h-9 w-auto" : "h-14 w-auto"} />
    </div>
  );
}

export function useFetch(fn, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fn()); setError(null); } catch (e) { setError(e); } finally { setLoading(false); }
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { data, error, loading, reload: load, setData };
}

export function Btn({ children, variant = "primary", className = "", loading, ...p }) {
  const v = {
    primary: "bg-blue-900 text-white hover:bg-blue-800", success: "bg-emerald-600 text-white hover:bg-emerald-700",
    outline: "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50", ghost: "text-slate-600 hover:bg-slate-100",
    danger: "bg-rose-600 text-white hover:bg-rose-700",
  }[variant];
  return (
    <button {...p} disabled={p.disabled || loading} className={`inline-flex h-9 items-center justify-center gap-2 rounded-md px-4 text-sm font-semibold transition-colors disabled:opacity-50 ${v} ${className}`}>
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}{children}
    </button>
  );
}
